import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaSpinner,
  FaClock,
  FaCheckCircle,
  FaArrowLeft,
  FaArrowRight,
  FaRocket,
  FaBookOpen,
  FaGraduationCap,
  FaChartLine,
  FaTrophy,
  FaUserGraduate,
  FaHome,
  FaFire,
} from "react-icons/fa";
import axios from "../utils/axiosConfig";
import toast from "react-hot-toast";
import Loader from "../components/common/Loader";
import { useFooter } from "../context/FooterContext";

const CourseTopics = () => {
  const { domainId } = useParams();
  const navigate = useNavigate();
  const { setShowFooter } = useFooter();
  const [topics, setTopics] = useState([]);
  const [domain, setDomain] = useState(null);
  const [currentTopic, setCurrentTopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    setShowFooter(false);
    fetchTopics();
    checkDarkMode();
    return () => setShowFooter(true);
  }, [domainId]);

  const checkDarkMode = () => {
    const dark = document.documentElement.getAttribute("data-theme") === "dark";
    setIsDarkMode(dark);
    const observer = new MutationObserver(() => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  };

  const fetchTopics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `/api/roadmap/domains/${domainId}/topics`,
      );
      setTopics(response.data.topics);
      setCurrentTopic(response.data.currentTopic);
      setDomain(response.data.domain);

      // Calculate overall progress
      const completed = response.data.topics.filter(
        (t) => t.progress.status === "completed",
      ).length;
      const total = response.data.topics.length;
      setProgress(total > 0 ? (completed / total) * 100 : 0);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Please enroll in this course first");
        navigate("/learning/my-learning");
      } else {
        toast.error("Failed to fetch topics");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStartTopic = async (topic) => {
    if (!topic.isUnlocked) {
      toast.error("Complete previous topics first to unlock this topic");
      return;
    }
    navigate(`/learning/topic/${topic._id}`);
  };

  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "rgba(255, 255, 255, 0.9)";
  const getProgressColor = (percentage) => {
    if (percentage >= 75) return "#10b981";
    if (percentage >= 50) return "#3b82f6";
    if (percentage >= 25) return "#f59e0b";
    return "#ef4444";
  };

  if (loading) return <Loader />;

  return (
    <div
      style={{
        minHeight: "100vh",
        background: isDarkMode ? "#0a0a0f" : "#f5f7fb",
      }}
    >
      {/* Modern Navigation Bar */}
      <div
        style={{
          background: getCardBackground(),
          backdropFilter: "blur(20px)",
          borderBottom: `1px solid ${getBorderColor()}`,
          position: "sticky",
          top: 0,
          zIndex: 100,
          padding: "1rem 2rem",
        }}
      >
        <div
          style={{
            maxWidth: "1200px",
            margin: "0 auto",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "1rem",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "1rem",
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={() => navigate("/learning/my-learning")}
              style={{
                background: "none",
                border: "none",
                color: "#8b5cf6",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                padding: "0.5rem 1rem",
                borderRadius: "8px",
                transition: "all 0.3s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "rgba(139,92,246,0.1)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "transparent")
              }
            >
              <FaArrowLeft /> My Learning
            </button>
            <button
              onClick={() => navigate("/dashboard")}
              style={{
                background: "none",
                border: "none",
                color: getTextSecondary(),
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                padding: "0.5rem 1rem",
                borderRadius: "8px",
                transition: "all 0.3s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "rgba(139,92,246,0.1)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "transparent")
              }
            >
              <FaHome /> Dashboard
            </button>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <span style={{ fontSize: "0.8rem", color: getTextSecondary() }}>
              <FaGraduationCap
                style={{ display: "inline", marginRight: "0.3rem" }}
              />
              {domain?.name}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: "1000px", margin: "0 auto", padding: "2rem" }}>
        {/* Course Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            background: "linear-gradient(135deg, #1e1b4b 0%, #2e1065 100%)",
            borderRadius: "24px",
            padding: "2rem",
            marginBottom: "2rem",
            color: "white",
          }}
        >
          <h1 style={{ fontSize: "1.8rem", marginBottom: "0.5rem" }}>
            {domain?.name}
          </h1>
          <p style={{ opacity: 0.9, marginBottom: "1rem" }}>
            {domain?.description}
          </p>

          {/* Progress Bar */}
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "0.5rem",
                fontSize: "0.8rem",
              }}
            >
              <span>Course Progress</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div
              style={{
                height: "8px",
                background: "rgba(255,255,255,0.2)",
                borderRadius: "10px",
                overflow: "hidden",
              }}
            >
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5 }}
                style={{
                  height: "100%",
                  background: "linear-gradient(90deg, #8b5cf6, #3b82f6)",
                  borderRadius: "10px",
                }}
              />
            </div>
          </div>
        </motion.div>

        {/* Current Topic Highlight */}
        {currentTopic && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              background:
                "linear-gradient(135deg, rgba(139,92,246,0.1), rgba(59,130,246,0.05))",
              borderRadius: "20px",
              padding: "1.5rem",
              marginBottom: "2rem",
              border: `1px solid ${getBorderColor()}`,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                marginBottom: "0.8rem",
              }}
            >
              <FaFire color="#f59e0b" />
              <span
                style={{
                  fontSize: "0.8rem",
                  color: "#f59e0b",
                  fontWeight: "500",
                }}
              >
                Continue Where You Left Off
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "1rem",
              }}
            >
              <div>
                <h3
                  style={{
                    color: getTextColor(),
                    marginBottom: "0.3rem",
                    fontSize: "1.2rem",
                  }}
                >
                  {currentTopic.title}
                </h3>
                <p
                  style={{
                    color: getTextSecondary(),
                    fontSize: "0.85rem",
                    maxWidth: "500px",
                  }}
                >
                  {currentTopic.description?.substring(0, 120)}...
                </p>
                <div
                  style={{
                    display: "flex",
                    gap: "1rem",
                    marginTop: "0.5rem",
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      fontSize: "0.7rem",
                      color: getTextSecondary(),
                      display: "flex",
                      alignItems: "center",
                      gap: "0.3rem",
                    }}
                  >
                    <FaClock size={10} /> {currentTopic.estimatedTime} min
                  </span>
                  {currentTopic.difficulty && (
                    <span
                      style={{
                        fontSize: "0.65rem",
                        padding: "0.2rem 0.5rem",
                        borderRadius: "4px",
                        background:
                          currentTopic.difficulty === "beginner"
                            ? "#10b98120"
                            : currentTopic.difficulty === "intermediate"
                              ? "#f59e0b20"
                              : "#ef444420",
                        color:
                          currentTopic.difficulty === "beginner"
                            ? "#10b981"
                            : currentTopic.difficulty === "intermediate"
                              ? "#f59e0b"
                              : "#ef4444",
                      }}
                    >
                      {currentTopic.difficulty}
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => handleStartTopic(currentTopic)}
                style={{
                  padding: "0.7rem 1.5rem",
                  background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  color: "white",
                  border: "none",
                  borderRadius: "12px",
                  cursor: "pointer",
                  fontWeight: "500",
                  whiteSpace: "nowrap",
                }}
              >
                Resume <FaArrowRight style={{ marginLeft: "0.3rem" }} />
              </button>
            </div>
          </motion.div>
        )}

        {/* Topics List */}
        <h2
          style={{
            color: getTextColor(),
            marginBottom: "1.5rem",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
          }}
        >
          <FaBookOpen /> Course Topics
        </h2>

        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {topics.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "3rem",
                color: getTextSecondary(),
              }}
            >
              No topics available in this course yet.
            </div>
          ) : (
            topics.map((topic, index) => (
              <motion.div
                key={topic._id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ scale: 1.01 }}
                onClick={() => topic.isUnlocked && handleStartTopic(topic)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                  padding: "1rem 1.5rem",
                  background: getCardBackground(),
                  borderRadius: "16px",
                  border: `1px solid ${getBorderColor()}`,
                  opacity: topic.isUnlocked ? 1 : 0.6,
                  cursor: topic.isUnlocked ? "pointer" : "not-allowed",
                  transition: "all 0.3s",
                }}
              >
                {/* Status Icon */}
                <div
                  style={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "50%",
                    background:
                      topic.progress.status === "completed"
                        ? "#10b981"
                        : topic.progress.status === "in_progress"
                          ? "#8b5cf6"
                          : isDarkMode
                            ? "rgba(255,255,255,0.1)"
                            : "#f1f5f9",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color:
                      topic.progress.status === "completed" ||
                      topic.progress.status === "in_progress"
                        ? "white"
                        : getTextSecondary(),
                    fontSize: "1.2rem",
                  }}
                >
                  {topic.progress.status === "completed" ? (
                    <FaCheckCircle />
                  ) : topic.progress.status === "in_progress" ? (
                    <FaRocket />
                  ) : (
                    <span>{index + 1}</span>
                  )}
                </div>

                {/* Topic Info */}
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.8rem",
                      flexWrap: "wrap",
                      marginBottom: "0.3rem",
                    }}
                  >
                    <h4
                      style={{
                        color: getTextColor(),
                        margin: 0,
                        fontSize: "1rem",
                      }}
                    >
                      {topic.title}
                    </h4>
                    {topic.progress.status === "completed" && (
                      <span
                        style={{
                          fontSize: "0.65rem",
                          color: "#10b981",
                          background: "#10b98120",
                          padding: "0.2rem 0.5rem",
                          borderRadius: "20px",
                        }}
                      >
                        Completed
                      </span>
                    )}
                    {topic.progress.status === "in_progress" && (
                      <span
                        style={{
                          fontSize: "0.65rem",
                          color: "#8b5cf6",
                          background: "#8b5cf620",
                          padding: "0.2rem 0.5rem",
                          borderRadius: "20px",
                        }}
                      >
                        In Progress
                      </span>
                    )}
                  </div>
                  <div
                    style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}
                  >
                    <span
                      style={{
                        fontSize: "0.7rem",
                        color: getTextSecondary(),
                        display: "flex",
                        alignItems: "center",
                        gap: "0.3rem",
                      }}
                    >
                      <FaClock size={10} /> {topic.estimatedTime} min
                    </span>
                    {topic.difficulty && (
                      <span
                        style={{
                          fontSize: "0.65rem",
                          padding: "0.2rem 0.5rem",
                          borderRadius: "4px",
                          background:
                            topic.difficulty === "beginner"
                              ? "#10b98120"
                              : topic.difficulty === "intermediate"
                                ? "#f59e0b20"
                                : "#ef444420",
                          color:
                            topic.difficulty === "beginner"
                              ? "#10b981"
                              : topic.difficulty === "intermediate"
                                ? "#f59e0b"
                                : "#ef4444",
                        }}
                      >
                        {topic.difficulty}
                      </span>
                    )}
                  </div>
                </div>

                {/* Action Button */}
                {topic.isUnlocked &&
                  topic.progress.status !== "completed" &&
                  topic.progress.status !== "in_progress" && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStartTopic(topic);
                      }}
                      style={{
                        padding: "0.5rem 1.2rem",
                        background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                        color: "white",
                        border: "none",
                        borderRadius: "8px",
                        cursor: "pointer",
                        fontSize: "0.8rem",
                        fontWeight: "500",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Start{" "}
                      <FaArrowRight
                        style={{ marginLeft: "0.3rem", fontSize: "0.7rem" }}
                      />
                    </button>
                  )}
                {!topic.isUnlocked && (
                  <span
                    style={{
                      color: "#f59e0b",
                      fontSize: "0.7rem",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <FaRocket style={{ marginRight: "0.3rem" }} /> Locked
                  </span>
                )}
              </motion.div>
            ))
          )}
        </div>
      </div>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default CourseTopics;
